import { c as _c } from "react/compiler-runtime";
import { type NavButtonsProps } from '@precis-digital/kurama/TopNavigation';
import { last } from 'ramda';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useChangePath, useCompatRouter, usePathname } from 'shared/components/Router';
const splitPathAndQueryParams = (url: string): PathState => {
  const queryParams: Record<string, string> = {};
  const urlQueryParams = url.split('?')[1];
  if (urlQueryParams != null) {
    new URLSearchParams(urlQueryParams).forEach((value, key) => {
      queryParams[key] = value;
    });
  }
  return {
    path: url.split('?')[0],
    query: queryParams
  };
};
interface PathState {
  path: string;
  query: Record<string, string>;
}
type NavigationState = Record<'historic' | 'future', PathState[]>;
export const useNavButtons = () => {
  const $ = _c(24);
  const compatRouter = useCompatRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      historic: [],
      future: []
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [navigationState, setNavigationState] = useState(t0);
  const {
    changePath
  } = useChangePath();
  const [hasClickedBackwards, setHasClickedBackwards] = useState(false);
  const navigationStateRef = useRef(navigationState);
  const hasClickedBackwardsRef = useRef(hasClickedBackwards);
  const {
    pathnameWithQueryParams
  } = usePathname();
  let t1;
  let t2;
  if ($[1] !== hasClickedBackwards || $[2] !== navigationState) {
    t1 = () => {
      navigationStateRef.current = navigationState;
      hasClickedBackwardsRef.current = hasClickedBackwards;
    };
    t2 = [navigationState, hasClickedBackwards];
    $[1] = hasClickedBackwards;
    $[2] = navigationState;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const {
        targetUrlState,
        previousUrlState,
        navigationState: navigationState_0,
        hasClickedBackwards: hasClickedBackwards_0
      } = t4;
      const lastHistoricPath = last(navigationState_0.historic);
      if (lastHistoricPath?.path === targetUrlState.path && hasClickedBackwards_0) {
        setHasClickedBackwards(false);
        setNavigationState(currentState => ({
          historic: currentState.historic.slice(0, -1),
          future: [...currentState.future, previousUrlState]
        }));
        return;
      }
      const lastFuturePath = last(navigationState_0.future);
      if (lastFuturePath?.path === targetUrlState.path) {
        setNavigationState(currentState_0 => ({
          historic: [...currentState_0.historic, previousUrlState],
          future: currentState_0.future.slice(0, -1)
        }));
        return;
      }
      setNavigationState(currentState_1 => ({
        historic: [...currentState_1.historic, previousUrlState],
        future: []
      }));
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const updateNavigationState = t3;
  const currentUrlRef = useRef(null);
  let t4;
  let t5;
  if ($[6] !== compatRouter || $[7] !== pathnameWithQueryParams) {
    t4 = () => {
      if (compatRouter != null) {
        return;
      }
      const targetUrlState_0 = splitPathAndQueryParams(pathnameWithQueryParams);
      const navigationState_1 = navigationStateRef.current;
      const hasClickedBackwards_1 = hasClickedBackwardsRef.current;
      const previousUrlState_0 = currentUrlRef.current;
      currentUrlRef.current = targetUrlState_0;
      if (previousUrlState_0 === null) {
        return;
      }
      if (previousUrlState_0.path === targetUrlState_0.path) {
        return;
      }
      updateNavigationState({
        targetUrlState: targetUrlState_0,
        previousUrlState: previousUrlState_0,
        navigationState: navigationState_1,
        hasClickedBackwards: hasClickedBackwards_1
      });
    };
    t5 = [pathnameWithQueryParams, compatRouter, updateNavigationState];
    $[6] = compatRouter;
    $[7] = pathnameWithQueryParams;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== compatRouter) {
    t6 = () => {
      if (compatRouter == null) {
        return;
      }
      const handleRouteChange = (url, t8) => {
        const {
          shallow
        } = t8;
        const targetUrlState_1 = splitPathAndQueryParams(url);
        const currentUrlState = splitPathAndQueryParams(compatRouter?.asPath ?? "");
        const navigationState_2 = navigationStateRef.current;
        const hasClickedBackwards_2 = hasClickedBackwardsRef.current;
        if (targetUrlState_1.path === currentUrlState.path) {
          return;
        }
        if (shallow) {
          return;
        }
        updateNavigationState({
          targetUrlState: targetUrlState_1,
          previousUrlState: currentUrlState,
          navigationState: navigationState_2,
          hasClickedBackwards: hasClickedBackwards_2
        });
      };
      compatRouter?.events.on("beforeHistoryChange", handleRouteChange);
      return () => {
        compatRouter?.events.off("beforeHistoryChange", handleRouteChange);
      };
    };
    t7 = [compatRouter, updateNavigationState];
    $[10] = compatRouter;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] !== changePath || $[14] !== navigationState.historic) {
    t8 = () => {
      const targetPath = last(navigationState.historic);
      if (targetPath != null) {
        setHasClickedBackwards(true);
        changePath(targetPath.path, targetPath.query);
      }
    };
    $[13] = changePath;
    $[14] = navigationState.historic;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleBackwardClick = t8;
  let t9;
  if ($[16] !== changePath || $[17] !== navigationState.future) {
    t9 = () => {
      const targetPath_0 = last(navigationState.future);
      if (targetPath_0 != null) {
        changePath(targetPath_0.path, targetPath_0.query);
      }
    };
    $[16] = changePath;
    $[17] = navigationState.future;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleForwardClick = t9;
  const t10 = navigationState.historic.length === 0;
  const t11 = navigationState.future.length === 0;
  let t12;
  if ($[19] !== handleBackwardClick || $[20] !== handleForwardClick || $[21] !== t10 || $[22] !== t11) {
    t12 = {
      handleBackwardClick,
      handleForwardClick,
      isBackwardDisabled: t10,
      isForwardDisabled: t11
    };
    $[19] = handleBackwardClick;
    $[20] = handleForwardClick;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};